import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const FLOAT_MENU = ({
  anchorEl,
  anchorPosition,
  anchorReference = "anchorEl",
  open,
  onClose,
  children,
  scrollContainerId, // Kept in props to avoid breaking your existing imports, but unused now
  maxHeight,
  zIndex = 1300,
}) => {
  const menuRef = useRef(null);
  const [render, setRender] = useState(open);
  const [animateOpen, setAnimateOpen] = useState(false);

  // 1. Mount/unmount logic with animation
  useEffect(() => {
    if (open) {
      setRender(true);
      setTimeout(() => setAnimateOpen(true), 10);
    } else {
      setAnimateOpen(false);
      const timeout = setTimeout(() => setRender(false), 180);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  // 2. Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        anchorReference === "anchorEl" &&
        anchorEl &&
        anchorEl.contains(event.target)
      ) {
        return;
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose, anchorEl, anchorReference]);

  // 3. Close on ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleKeyDown, { capture: true });
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [open, onClose]);

  // 4. Viewport-Aware Positioning & Scroll/Resize Tracking
  useEffect(() => {
    if (!render) return;

    const updatePosition = () => {
      const menu = menuRef.current;
      if (!menu) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      const menuWidth = menu.offsetWidth;
      // Using scrollHeight gets the true unconstrained height of the content
      const menuHeight = menu.scrollHeight;

      let top = 0;
      let left = 0;
      let calculatedMaxHeight = maxHeight || viewportHeight - 20;

      const PADDING = 10; // 10px breathing room from the edges of the screen

      // MODE 1: Pixel Coordinates
      if (anchorReference === "anchorPosition" && anchorPosition) {
        let vLeft = anchorPosition.left;
        let vTop = anchorPosition.top;

        if (vLeft + menuWidth > viewportWidth - PADDING)
          vLeft = viewportWidth - menuWidth - PADDING;
        if (vLeft < PADDING) vLeft = PADDING;

        if (vTop + menuHeight > viewportHeight - PADDING)
          vTop = viewportHeight - menuHeight - PADDING;
        if (vTop < PADDING) vTop = PADDING;

        left = vLeft + scrollX;
        top = vTop + scrollY;
      }
      // MODE 2: HTML Element Binding
      else if (
        anchorReference === "anchorEl" &&
        anchorEl &&
        anchorEl.getBoundingClientRect
      ) {
        const rect = anchorEl.getBoundingClientRect();
        let vLeft = rect.left;
        let vTop = rect.bottom;

        // -- HORIZONTAL BOUNDARIES --
        if (vLeft + menuWidth > viewportWidth - PADDING) {
          // Shift it left to align with the right edge of the anchor
          vLeft = rect.right - menuWidth;
          // If it STILL spills left (e.g., huge menu), clamp to edge
          if (vLeft < PADDING) {
            vLeft = PADDING;
          }
        }

        // -- VERTICAL BOUNDARIES --
        const spaceBelow = viewportHeight - rect.bottom - PADDING;
        const spaceAbove = rect.top - PADDING;

        if (vTop + menuHeight > viewportHeight - PADDING) {
          // If there's more space above than below, flip the menu upwards
          if (spaceAbove > spaceBelow) {
            vTop = rect.top - menuHeight;
            calculatedMaxHeight = spaceAbove;

            // Failsafe clamp
            if (vTop < PADDING) {
              vTop = PADDING;
            }
          } else {
            // Keep it below, but squeeze the height so it becomes scrollable
            calculatedMaxHeight = spaceBelow;
          }
        }

        left = vLeft + scrollX;
        top = vTop + scrollY;
      } else {
        return; // No valid anchor provided
      }

      // Respect manual maxHeight prop if provided and stricter than calculated
      if (maxHeight && calculatedMaxHeight > maxHeight) {
        calculatedMaxHeight = maxHeight;
      }

      // -- DIRECT DOM MUTATION FOR SCROLL PERFORMANCE --
      // Avoids triggering React state re-renders on every scroll tick
      menu.style.top = `${top}px`;
      menu.style.left = `${left}px`;
      menu.style.maxHeight =
        typeof calculatedMaxHeight === "number"
          ? `${calculatedMaxHeight}px`
          : calculatedMaxHeight;
      menu.style.overflowY = "auto";
    };

    // Run immediately on mount
    updatePosition();

    // Attach ResizeObserver to watch for shrinking/growing content dynamically
    let resizeObserver;
    if (menuRef.current) {
      resizeObserver = new ResizeObserver(() => updatePosition());
      resizeObserver.observe(menuRef.current);
    }

    // Keep it glued when resizing OR scrolling parents
    // passive: true improves scroll performance
    // capture: true ensures we catch scroll events from ANY nested scrollable div, not just the window
    window.addEventListener("resize", updatePosition, { passive: true });
    window.addEventListener("scroll", updatePosition, {
      capture: true,
      passive: true,
    });

    // Cleanup observers and listeners
    return () => {
      if (resizeObserver && menuRef.current) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, { capture: true });
    };
  }, [render, anchorEl, anchorPosition, anchorReference, maxHeight]);

  if (!render) return null;

  // NOTE: Static `top`, `left`, `maxHeight` were removed from this inline style block.
  // They are now managed highly efficiently via direct DOM manipulation in `updatePosition`.
  const menuElement = (
    <div
      ref={menuRef}
      style={{
        position: "absolute",
        zIndex: zIndex,
        background: "#fff",
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: "13px",
        padding: "13px",
        transformOrigin: "top left",
        transition: "transform 180ms ease-out, opacity 180ms ease-out",
        transform: animateOpen ? "scale(1)" : "scale(0.8)",
        opacity: animateOpen ? 1 : 0,
        pointerEvents: animateOpen ? "auto" : "none",
        maxWidth: "80vw",
        // overflowY is applied dynamically in updatePosition
      }}
    >
      {children}
    </div>
  );

  return createPortal(menuElement, document.body);
};

export default FLOAT_MENU;
